import { useNavigator } from '@repo/toolkit/use-navigator'
import CapsuleTab from '@repo/ui/capsule-tab'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { getCenter } from '../../../api/centers/get-center'
import { getCenterFinalCostVisibility } from '../../../api/centers/get-center-final-cost-visibility'
import { getOlCenterTabs } from '../../../lib/constants/ol-permissions'
import useAuth from '../../../lib/hooks/use-auth'
import useTranslate from '../../../lib/hooks/use-translate'

type Props = {
  centerId: number
}

const OlCenterCapsuleTab: React.FC<Props> = ({ centerId }) => {
  const [activeTab, setActiveTab] = useState(-1)
  const { user } = useAuth()
  const { pathname } = useLocation()
  const { navigate } = useNavigator()
  const { t } = useTranslate()

  const { data: center } = useQuery({
    queryKey: ['center', centerId],
    queryFn: () => getCenter(centerId, 'COMPACT')
  })

  const { data: finalCostVisibility } = useQuery({
    queryKey: ['final-cost-visibility', centerId],
    queryFn: () => getCenterFinalCostVisibility(centerId)
  })

  const tagConfig = useMemo(
    () =>
      getOlCenterTabs({
        role: user.role,
        type: center?.ol?.type,
        finalCostVisibility
      }),
    [user.role, center?.ol?.type, finalCostVisibility]
  )

  // map the tagConfig to the tabs
  const tabs = useMemo(() => {
    if (!center) return []
    return tagConfig.map((tab) => ({ ...tab, label: t(tab.label) }))
  }, [tagConfig, t])

  useEffect(() => {
    // set the active tab based on the pathname
    for (const tab of Object.values(tagConfig))
      if (pathname.includes(tab.path)) {
        setActiveTab(tab.index)
        return
      }
  }, [pathname])

  useEffect(() => {
    if (activeTab !== -1) return
    // on page load, set the active tab based on the pathname
    const index = tagConfig.findIndex((tab) => pathname.includes(tab.path))
    if (index !== -1) {
      setActiveTab(index)
      return
    }
  }, [tagConfig])

  return (
    <div>
      {/** Tabs for the center details */}
      <CapsuleTab
        tabs={tabs}
        variant="secondary"
        wrapLabels
        activeTab={activeTab}
        onTabChange={(index) => {
          setActiveTab(index)
          const targetPath = tagConfig.find((tab) => tab.index === index)?.path
          // If the user already navigated to the tab, we don't want to navigate again
          if (targetPath && pathname.includes(targetPath)) return
          else navigate(tagConfig[index].path)
        }}
      />
    </div>
  )
}

export default OlCenterCapsuleTab
